/**
 * The shift report's face - React edition of ui/ShiftReport.ts's render half.
 * The pedagogy (commit before the reveal, readings cost time, a wrong call is
 * the repair twice) lives in tools/ShiftState.ts; this pane is a projection
 * of it, and the counterfactual table in the verdict is still the part that
 * matters most.
 */

import { sci } from '../../core/sci'
import { t, tOr } from '../../i18n/i18n'
import type { Journal } from '../../learn/Journal'
import { localiseAspect, localiseFault, localiseReadings } from '../../sim/faults/localise'
import { inSpec, type ToolReading } from '../../sim/faults/model'
import type { ShiftState } from '../../tools/ShiftState'
import { HelpTip } from '../../tools/Workbench'
import { useLang } from '../../app/i18n/LanguageProvider'
import { usePanelRevision } from './usePanelState'
import { DebriefBlock } from './DebriefSection'
import { useTick } from '../../app/state/useObservable'

function fmtNum(v: number): string {
  if (!Number.isFinite(v)) return '--'
  const a = Math.abs(v)
  if (a !== 0 && a < 0.01) return sci(v, 2)
  return a >= 100 ? v.toFixed(1) : a >= 10 ? v.toFixed(2) : v.toFixed(3)
}

function ReadingRow({ r }: { r: ToolReading }) {
  return (
    <div className={`reading-row ${r.state ?? ''}`}>
      <span className="reading-label">{r.label}</span>
      <span className="reading-value">{r.value}</span>
      {r.limit && <span className="reading-limit">{r.limit}</span>}
    </div>
  )
}

/** The opening sentence of a catalogue string, in whatever language: up to the first stop followed by a space, or a CJK stop. */
function firstSentence(text: string): string {
  const end = text.search(/[.!?](?=\s)|[。！？]/)
  return end >= 0 ? text.slice(0, end + 1) : text
}

export function ShiftReportPane<A extends string, C>({
  shift,
  journal,
}: {
  shift: ShiftState<A, C>
  journal: Journal
}) {
  useLang()
  usePanelRevision(shift)
  // The embedded debrief follows the journal (readings and calls land there).
  useTick((fn) => journal.subscribe(fn))

  const model = shift.model
  const aspectText = (id: string, label: string, action: string) =>
    localiseAspect(model.toolId, id, label, action)
  const readingLabel = (r: { id: string; label: string }): string =>
    localiseReadings(model.toolId, [r as never])[0].label

  const m = model.evaluate(shift.condition)
  const ships = inSpec(model, shift.condition)
  const faultText = shift.faultText
  const earned = model.earnedReadings(shift.condition)
  const spent = shift.hoursOnReadings()

  const verdict = () => {
    const call = shift.call!
    const called = model.aspects.find((a) => a.id === call.aspect)
    const truth = model.aspects.find((a) => a.id === shift.fault.aspect)
    const d = shift.diagnosis()
    const repairHours = call.correct
      ? (called?.downtimeHours ?? 0)
      : (called?.downtimeHours ?? 0) + (truth?.downtimeHours ?? 0)
    const scrapped = journal.scrappedWafers()

    return (
      <div style={{ marginTop: 14 }}>
        <div
          className="pending-note"
          style={call.correct ? { borderLeftColor: 'var(--good)' } : undefined}
        >
          <div
            style={{
              color: call.correct ? 'var(--good)' : 'var(--critical)',
              fontSize: 13,
              marginBottom: 7,
            }}
          >
            {t(call.correct ? 'shift.Correct' : 'shift.Wrong', { label: called?.label ?? '' })}
          </div>
          <div>{faultText.truth}</div>
          <ul className="scope-list" style={{ marginTop: 9 }}>
            {faultText.evidence.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.55 }}>
            {call.correct
              ? t('shift.RepairRight', { action: called?.action ?? '', hours: repairHours })
              : t('shift.RepairWrong', {
                  action: called?.action ?? '',
                  hours: called?.downtimeHours ?? 0,
                  realAction: truth?.action ?? t('shift.TheRealRepair'),
                  realHours: truth?.downtimeHours ?? 0,
                  total: repairHours,
                })}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.55 }}>
            {t('shift.ThisShift', {
              investigating: spent.toFixed(1),
              repairing: repairHours,
              scrapped,
            })}
          </div>
        </div>

        <div className="blk" style={{ marginTop: 14 }}>
          {/* The heading says what the rows are; how they were made waits
              behind the mark (tools/Workbench.tsx, HelpTip). */}
          <div className="k">
            {t('shift.WhatEachRepair')}
            <HelpTip>{t('shift.WhatEachRepairNote')}</HelpTip>
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          {d.ranked.map((tr) => (
            <div
              key={tr.aspect}
              className="reading-row"
              style={
                tr.aspect === shift.fault.aspect
                  ? { borderLeft: '2px solid var(--good)', paddingLeft: 7 }
                  : undefined
              }
            >
              <span className="reading-label">
                {`${aspectText(tr.aspect, tr.label, tr.action).label} (${tr.downtimeHours} h)`}
              </span>
              <span className="reading-value">
                {t('shift.Recovered', {
                  pct: (tr.recovery * 100).toFixed(0),
                  primary: `${fmtNum(tr.primary)} ${d.asFound.unit}`,
                  secondary: `${fmtNum(tr.secondary)} ${model.secondaryUnit}`,
                })}
              </span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 9, fontSize: 11, color: 'var(--ink-muted)', lineHeight: 1.5 }}>
          {t(d.separation > 0.15 ? 'shift.Decidable' : 'shift.TooClose', {
            points: (d.separation * 100).toFixed(0),
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="shift-report">
      {/* TWO COLUMNS ON A WIDE PANE: what the student is TOLD on the left (the
          complaint, the tool's own screen), what they DO about it on the
          right (the readings that cost time, then the call). Stacked, this
          pane ran 800-1000 px in every tool - the call buttons a screen
          below the complaint they answer. Under ~720 px it is one column,
          in the old order. Nothing is removed and nothing reordered within
          a column. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', columnGap: 24, rowGap: 14, alignItems: 'start' }}>
      <div>
      {/* The complaint - handed over with no diagnosis attached. */}
      <div className="blk">
        <div className="k">{t('shift.Title', { tool: shift.toolName })}</div>
        <div style={{ fontSize: '12.5px', color: 'var(--ink)', lineHeight: 1.55, marginTop: 6 }}>
          {'“' + faultText.complaint + '”'}
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: '11.5px',
            color: ships ? 'var(--good)' : 'var(--critical)',
          }}
        >
          {t(ships ? 'shift.AsFoundInSpec' : 'shift.AsFoundOutOfSpec', {
            primary: `${fmtNum(m.primary)} ${m.unit}`,
            secondary:
              `${tOr(`tool.${model.toolId}.SecondaryLabel`, model.secondaryLabel).toLowerCase()} ` +
              `${fmtNum(m.secondary)} ${model.secondaryUnit}`,
          })}
        </div>
      </div>

      {/* Free readings - the tool's own screen. */}
      <div style={{ marginTop: 14 }}>
        <div className="blk">
          <div className="k">{t('shift.AtTheTool')}</div>
        </div>
        <div style={{ marginTop: 8 }}>
          {localiseReadings(model.toolId, model.readings(shift.condition)).map((r) => (
            <ReadingRow key={r.id} r={r} />
          ))}
        </div>
      </div>

      </div>
      <div>
      {/* Earned readings, each behind its own button. An untaken reading
          shows its label and its cost and nothing else - the only way the
          choice of which one to take can mean anything. */}
      <div>
        <div className="blk">
          {/* The heading already says these cost tool time. The advice on
              which to take is behind the mark; once hours have been spent,
              the RUNNING TOTAL stays on the screen - the opening sentence of
              the same string, so no catalogue needs a second one - and the
              rest of it joins the advice in the tip. */}
          <div className="k">
            {t('shift.Investigate')}
            <HelpTip>
              {spent > 0
                ? t('shift.SpentSoFar', { hours: spent.toFixed(1) }) + '\n\n' + t('shift.InvestigateNote')
                : t('shift.InvestigateNote')}
            </HelpTip>
          </div>
          {spent > 0 && (
            <div style={{ fontSize: 11, color: 'var(--ink-muted)', lineHeight: 1.5, marginTop: 5 }}>
              {firstSentence(t('shift.SpentSoFar', { hours: spent.toFixed(1) }))}
            </div>
          )}
        </div>
        <div style={{ marginTop: 8 }}>
          {earned.map((r) =>
            shift.taken.has(r.id) ? (
              <ReadingRow key={r.id} r={localiseReadings(model.toolId, [r])[0]} />
            ) : (
              <div key={r.id} className="reading-row pending">
                <span className="reading-label">{readingLabel(r)}</span>
                <button className="btn tiny" onClick={() => shift.takeReading(r)}>
                  {t('shift.TakeIt')}
                </button>
              </div>
            ),
          )}
        </div>
      </div>

      {/* Commit before the reveal - or the verdict with its counterfactual. */}
      {shift.call ? (
        verdict()
      ) : (
        <div style={{ marginTop: 14 }}>
          <div className="blk">
            <div className="k">
              {t('shift.CallIt')}
              <HelpTip>{t('shift.CallItNote')}</HelpTip>
            </div>
          </div>
          <div className="grid-2" style={{ marginTop: 9 }}>
            {model.aspects.map((a) => (
              <button
                key={a.id}
                className="btn"
                style={{ textAlign: 'left' }}
                onClick={() => shift.commit(a.id)}
              >
                {`${aspectText(a.id, a.label, a.action).label} — ${a.downtimeHours} h`}
              </button>
            ))}
          </div>
        </div>
      )}

      </div>
      </div>

      {/* Always, not only after a call: the running cost of being careful. */}
      <DebriefBlock journal={journal} tool={shift.toolName} />

      {/* Instructor controls - in the panel, not behind a mode: the intended
          use is an instructor beside a student, arming the next scenario. */}
      <div style={{ marginTop: 16 }}>
        <div className="btn-row" style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={() => shift.startShift()}>
            {t('shift.StartShift')}
          </button>
          <button className="btn" onClick={() => shift.toggleReveal()}>
            {t(shift.revealed ? 'shift.HideAnswer' : 'shift.RevealCause')}
          </button>
        </div>
        <div className="blk">
          <div className="k">{t('shift.Instructor')}</div>
        </div>
        <select
          style={{
            width: '100%',
            background: 'var(--surface-raised)',
            color: 'var(--ink)',
            border: '1px solid var(--hair)',
            borderRadius: 3,
            padding: '6px 8px',
            font: 'inherit',
            fontSize: 12,
          }}
          value={shift.fault.id}
          onChange={(e) => shift.startShift(e.target.value)}
        >
          {model.faults.map((f) => (
            <option key={f.id} value={f.id}>
              {localiseFault(model.toolId, f).label}
            </option>
          ))}
        </select>
        {shift.revealed && (
          <div className="pending-note" style={{ marginTop: 10 }}>
            <div>{faultText.truth}</div>
            <ul className="scope-list" style={{ marginTop: 8 }}>
              {faultText.evidence.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
